import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Trans, withTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { withApollo } from '@apollo/client/react/hoc';
import { endOfDay, format, formatDistanceToNow, startOfDay, subDays } from 'date-fns';
import CommitStatus from '../../components/CommitStatus';
import Icon from '../../components/Icon';
import LoadingPlaceholder from '../../components/LoadingPlaceholder';
import MaintenanceBanner from '../../components/MaintenanceBanner';
import RepositoryDetailDrawer from '../../components/RepositoryDetailDrawer';
import Setup from '../../components/Setup';
import { HANDLED_ERRORS } from '../../constants/types';
import Layout from '../../containers/Layout';
import { getVcsIdBySlug, getVcsPrLink, VCS_SLUG } from '../../constants/api';
import { IS_APP_CODEAC } from '../../constants/env';
import PrMetrics from './containers/PrMetrics';
import PrList from './containers/PrList';
import ErrorPageContent from '../../components/ErrorPageContent';
import { handleBranchInUrl } from '../../utils/repositoryNameAndOwner';
import { getRepositoryFullnameFromMatch } from '../../utils/getRepository';
import * as types from '../../constants/ActionTypes';
import ApiV2ModelContext from '../../contexts/ApiV2ModelContext';
import PullRequestList from '../../model/entities/PullRequestList';
import { replaceAsciiCharWithSlash } from '../../utils/resolveBranch';
class PullRequestsPage extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            loading: true,
            pullRequestList: new PullRequestList([]),
            loadTimeout: null,
            processingPercentage: {},
            notFound: false,
            showErrorMessage: false,
        };
        this.loadRepositoryData = async () => {
            const { match, history, dispatch } = this.props;
            const repositoryFullname = replaceAsciiCharWithSlash(getRepositoryFullnameFromMatch(match));
            const vcs = getVcsIdBySlug(match.params.vcs);
            try {
                const dashboardResponse = await this.context.getDashboard(vcs, repositoryFullname);
                if (dashboardResponse?.repository) {
                    const repositoryData = dashboardResponse.repository;
                    const fullName = repositoryData.getFullName();
                    handleBranchInUrl(history, match, fullName, repositoryData.slug);
                    await this.loadPullRequests(repositoryData);
                    await dispatch({
                        type: types.ADD_REPOSITORY,
                        payload: dashboardResponse,
                    });
                    const activeBranch = match.params.branch ? dashboardResponse.repository.getBranchByName(match.params.branch) : dashboardResponse.repository.getMainBranch();
                    await dispatch({
                        type: types.UPDATE_ACTIVE_BRANCH,
                        payload: { repositoryId: repositoryData.id, activeBranch },
                    });
                }
                else {
                    this.setState({
                        ...dashboardResponse,
                        loading: false,
                    });
                }
            }
            catch (error) {
                const statusCode = error.networkError?.statusCode;
                if (HANDLED_ERRORS.includes(statusCode)) {
                    this.setState({
                        notFound: true,
                        loading: false,
                    });
                }
                else {
                    throw error;
                }
            }
        };
        this.loadPullRequests = async (repositoryData) => {
            const { repository } = this.props;
            const repositoryObject = repositoryData ?? repository;
            if (repositoryObject && Object.keys(repositoryObject).length > 1) {
                try {
                    const pullRequestsResponse = await this.context.getPullRequestList(repositoryObject.id);
                    if (pullRequestsResponse.pullRequests.length) {
                        this.setState({
                            pullRequestList: pullRequestsResponse,
                            notFound: false,
                            loading: false,
                        }, this.checkRunningAnalysis);
                    }
                    else {
                        this.setState({
                            loading: false,
                        });
                    }
                }
                catch (error) {
                    const statusCode = error.networkError?.statusCode;
                    if ([400, 403, 404].includes(statusCode)) {
                        this.setState({
                            notFound: true,
                            loading: false,
                        });
                    }
                    else if ([500, 501, 502].includes(statusCode)) {
                        this.setState({
                            showErrorMessage: true,
                            loading: false,
                        });
                    }
                    else {
                        throw error;
                    }
                }
            }
        };
        this.checkRunningAnalysis = () => {
            const { pullRequestList } = this.state;
            const hasRunningAnalysis = pullRequestList.hasAnyRunningAnalysis();
            const processingPercentage = pullRequestList.getCommitsPercentage();
            this.setState({
                processingPercentage,
            });
            if (hasRunningAnalysis) {
                const loadTimeout = setTimeout(async () => {
                    await this.loadPullRequests();
                }, 3000);
                this.setState({
                    // @ts-expect-error TS2322: Type 'Timeout' is not assignable to type 'number'.
                    loadTimeout,
                });
            }
        };
    }
    async componentDidMount() {
        const { repository } = this.props;
        if (!repository) {
            await this.loadRepositoryData();
        }
        await this.loadPullRequests();
    }
    componentWillUnmount() {
        if (this.state.loadTimeout) {
            clearTimeout(this.state.loadTimeout);
        }
    }
    renderPullRequests(fullName) {
        const { match, t } = this.props;
        const { processingPercentage, loading, pullRequestList } = this.state;
        return (_jsx(_Fragment, { children: pullRequestList.pullRequests.map((pullRequest) => {
                const prLink = `/${match.params.vcs}/${fullName}/pull-requests/${pullRequest.prId}`;
                return (_jsx("div", { className: "reports__item", children: _jsxs("div", { className: "d-flex", children: [_jsxs(Link, { className: "reports__link d-flex flex-grow-1", to: prLink, children: [_jsx("div", { children: !pullRequest.user ? null : (_jsx("img", { src: pullRequest.getUserAvatar(), className: "rounded-circle mr-2", height: "32" })) }), _jsxs("div", { className: "d-flex flex-column lh-1", children: [_jsxs("div", { children: [_jsx("strong", { children: pullRequest.title }), _jsx(Icon, { name: "long-arrow-right", className: "ml-2 mr-2 text-lightBlack" }), pullRequest.destinationBranchName] }), _jsx("div", { children: _jsxs("small", { className: "text-lightBlack", children: [pullRequest.user ? pullRequest.user.displayName + ' – ' : '', "#", pullRequest.prId, ",", ' ' + t('lastUpdated').toLocaleLowerCase() + ': ', _jsx("span", { title: format(new Date(pullRequest.prUpdatedOn), t('dateTimeLongFormat')), children: formatDistanceToNow(new Date(pullRequest.prUpdatedOn), { addSuffix: true }) })] }) })] }), _jsx("div", { className: "text-right align-self-center flex-grow-1", children: _jsx(CommitStatus, { entity: pullRequest, processingPercentage: processingPercentage[pullRequest.id], isPullRequest: true, loading: loading }) })] }), _jsx("div", { className: "pl-3 align-self-center d-none d-md-inline-block", children: _jsx("a", { href: getVcsPrLink(match.params.vcs, fullName, pullRequest.prId), target: "_blank", rel: "noreferrer", children: _jsx(Icon, { name: "external-link", fixedWidth: true }) }) })] }) }, pullRequest.prId));
            }) }));
    }
    renderPlaceholder() {
        const { t } = this.props;
        return (_jsxs("div", { className: "loading-container text-center", children: [_jsx("div", { className: "pull-request__loader", children: _jsx(LoadingPlaceholder, { type: "pullRequest", count: 10 }) }), _jsxs("span", { className: "sr-only", children: [t('loading'), "..."] })] }));
    }
    renderNoPRs() {
        const { t } = this.props;
        return (_jsxs("div", { className: "content content--no-pull-requests-page", children: [_jsx("span", { className: "content__picture", children: "\u00A0" }), _jsx("h2", { className: "content__title", children: t('noPullRequest') }), _jsx("p", { className: "content__text", children: _jsxs(Trans, { i18nKey: "watchingOpenPullRequests", values: { siteName: t('siteName') }, children: [t('siteName'), " is continuously watching your pull requests coming from this repository.", _jsx("br", {}), "Right now you have no ", _jsx("strong", { children: "open" }), " pull requests in this repository."] }) })] }));
    }
    renderLegacyPRList(fullName) {
        const { repository, t } = this.props;
        const { loading, pullRequestList } = this.state;
        if (loading) {
            return this.renderPlaceholder();
        }
        if (!loading && !pullRequestList.pullRequests.length) {
            return this.renderNoPRs();
        }
        return (_jsxs("div", { className: "report-detail__items mt-1 container", children: [_jsx("h4", { children: t('pullRequests') }), repository && this.renderPullRequests(fullName)] }));
    }
    renderNewPRList() {
        const { user, repository } = this.props;
        const duration = 29; // days
        const from = startOfDay(subDays(new Date(), duration));
        const to = endOfDay(new Date());
        const repositoryIds = repository?.id ? [repository.id] : [];
        return (_jsxs("div", { className: "report-detail__items mt-1 container", children: [repositoryIds.length && user.loggedIn ? (_jsx(PrMetrics, { activeRepositoriesIds: repositoryIds, from: from, to: to, showTimeInfo: true, isClickable: true })) : null, repository && this.renderList()] }));
    }
    renderList() {
        const { loading, pullRequestList } = this.state;
        const { repositories, user, repository } = this.props;
        if (!user) {
            return null;
        }
        const repos = repositories.list[VCS_SLUG[user.user?.vcs]] ?? [];
        const reposSlugsWithHooks = Object.keys(repos).filter((slug) => repos[slug].active);
        const repositoriesSlugs = Object.assign({}, ...reposSlugsWithHooks.map((slug) => ({ [repos[slug].id]: slug })));
        if (!loading && !pullRequestList.pullRequests.length) {
            return this.renderNoPRs();
        }
        return (_jsx(PrList, { pullRequestList: pullRequestList, repository: repository, repos: repos, repositoriesSlugs: repositoriesSlugs, loading: loading, title: "All Open Pull Requests" }));
    }
    render() {
        const { match, user, repository, t } = this.props;
        const { notFound, loading, showErrorMessage } = this.state;
        if (!repository) {
            return null;
        }
        const fullName = repository.getFullName();
        const isLoginRequired = !!repository.private;
        const { titlePrimary, titleSecondary } = repository.getTitleParts();
        if (loading && notFound) {
            return _jsx(ErrorPageContent, { ...this.props, code: 404 });
        }
        return (_jsxs(Layout, { title: t('pullRequests') + ' - ' + titlePrimary, loginRequired: isLoginRequired, withDrawer: true, match: match, titlePrimary: titlePrimary, titleSecondary: titleSecondary, repository: repository, children: [_jsx("div", { className: "container repository-detail__container", children: _jsxs("div", { className: "row", children: [_jsx(RepositoryDetailDrawer, { vcsSlug: match.params.vcs, fullName: fullName, className: "col col-12 col-md-2", isUserLoggedIn: user.loggedIn }), _jsxs("div", { className: "col col-12 col-md-10 repository-detail__content", children: [repository?.vcs && _jsx(MaintenanceBanner, { vcs: repository.vcs }), _jsx("div", { className: "d-flex flex-column flex-md-row", children: _jsx("div", { className: "col-12 col-md-8 col-lg-10 p-0", children: IS_APP_CODEAC ? ('') : (_jsxs(_Fragment, { children: [_jsx("div", { className: "repository-detail__owner", children: titleSecondary }), _jsx("h2", { className: "repository-detail__title", children: titlePrimary })] })) }) }), showErrorMessage && (_jsx("div", { className: "maintenance-banner alert alert-warning", children: t('graphqlErrorMessage') })), IS_APP_CODEAC ? this.renderNewPRList() : this.renderLegacyPRList(fullName)] })] }) }), repository?.private && _jsx(Setup, { loading: loading })] }));
    }
}
PullRequestsPage.contextType = ApiV2ModelContext;
const mapStateToProps = (state) => ({
    repositories: state.repositories,
    user: state.user,
    repository: state.repository.repository,
});
const mapDispatchToProps = (dispatch) => ({
    dispatch: dispatch,
});
export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(withApollo(PullRequestsPage)));
